import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, Phone, Plus, CalendarHeart, ChevronLeft } from "lucide-react";
import { PageShell } from "@/components/knmc/PageShell";
import type { DepartmentDetail as DepartmentDetailType } from "@/hooks/useDepartments";
import type { DepartmentListItem } from "@/hooks/useDepartments";
import type { Doctor } from "@/hooks/useDoctors";
import { getDepartmentIcon } from "@/lib/departmentIcons";
import { departmentUrl } from "@/lib/slugs";
import { DoctorCard } from "@/components/knmc/DoctorCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

export default function DepartmentDetail({ department: dept, departments, doctors}: { department: DepartmentDetailType; departments: DepartmentListItem[]; doctors: Doctor[]}) {
  if (!dept) return null;

  const Icon = getDepartmentIcon(dept.icon);
  const specialists = doctors.filter((d) => d.department === dept.department);

  return (
    <PageShell>

      <section className="relative overflow-hidden bg-linear-to-br from-primary via-primary to-secondary text-primary-foreground">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-24 lg:grid-cols-[1.4fr_1fr] lg:items-center lg:py-28">
          <div>
            <Link to="/departments" className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary-foreground/70 transition-colors hover:text-gold">
              <ChevronLeft className="h-4 w-4" /> All departments
            </Link>
            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-10 bg-gold" />
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Centre of Excellence</p>
            </div>
            <h1 className="font-display text-5xl font-semibold leading-[1.02] md:text-6xl lg:text-7xl">{dept.department}</h1>
            <p className="mt-5 max-w-xl font-display text-xl italic text-primary-foreground/80">{dept.tagline}</p>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-primary-foreground/75">{dept.description}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold/90">
                <Link to="/doctors"><CalendarHeart className="mr-2 h-4 w-4" />Book Appointment</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10">
                <a href="tel:+9771000000"><Phone className="mr-2 h-4 w-4" />Call Reception</a>
              </Button>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="relative mx-auto flex h-80 w-80 items-center justify-center rounded-full border border-gold/30 bg-primary-foreground/5 backdrop-blur">
              <div className="absolute inset-6 rounded-full border border-gold/20" />
              <div className="absolute inset-12 rounded-full border border-gold/10" />
              <Icon className="h-32 w-32 text-gold" strokeWidth={1.2} />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6">
          {dept.stats.map((s) => (
            <div key={s.label} className="px-4 py-8 text-center">
              <p className="font-display text-3xl font-semibold text-primary md:text-4xl">{s.value}</p>
              <p className="mt-1 text-xs font-medium uppercase tracking-widest text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-[1.4fr_1fr]">
          <div>
            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-10 bg-gold" />
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Overview</p>
            </div>
            <h2 className="font-display text-3xl font-semibold text-primary md:text-4xl">About the department</h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
              {dept.about.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-8 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Sub-specialties</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {dept.specialties.map((s) => (
                <span key={s} className="rounded-full border border-border bg-primary-soft px-3 py-1.5 text-xs font-medium text-primary">{s}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-surface py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-14 max-w-2xl">
            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-10 bg-gold" />
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Services</p>
            </div>
            <h2 className="font-display text-3xl font-semibold text-primary md:text-4xl">What we offer</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {dept.services.map((s) => (
              <div key={s.title} className="group relative overflow-hidden rounded-2xl border border-border/70 bg-card p-7 shadow-soft transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:shadow-elevated">
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary-soft text-primary">
                  <Check className="h-5 w-5" strokeWidth={2} />
                </div>
                <h3 className="font-display font-space text-lg font-semibold text-foreground transition-colors group-hover:text-primary">{s.title}</h3>
                <span aria-hidden className="mt-3 block h-px w-10 bg-gold/40 transition-all duration-500 group-hover:w-20 group-hover:bg-gold" />
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-14 max-w-2xl">
            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-10 bg-gold" />
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Facilities</p>
            </div>
            <h2 className="font-display text-3xl font-semibold text-primary md:text-4xl">Built for capable care</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {dept.facilities.map((f) => (
              <div key={f.title} className="group relative overflow-hidden rounded-2xl border border-border/70 bg-linear-to-br from-card to-primary-soft/30 p-7 shadow-soft">
                <Plus aria-hidden className="absolute right-5 top-5 h-4 w-4 text-gold/50 transition-transform duration-500 group-hover:rotate-90" strokeWidth={1.5} />
                <h3 className="font-display font-space text-lg font-semibold text-foreground">{f.title}</h3>
                <span aria-hidden className="mt-3 block h-px w-10 bg-gold/40" />
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-surface py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <div className="mb-5 flex items-center gap-3">
                <span className="h-px w-10 bg-gold" />
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Our Specialists</p>
              </div>
              <h2 className="font-display text-3xl font-semibold text-primary md:text-4xl">Meet the {dept.department} team</h2>
            </div>
            <Link to="/doctors" className="group inline-flex items-center gap-2 rounded-full border border-primary/20 bg-card px-5 py-3 text-sm font-semibold text-primary shadow-soft transition-all hover:border-primary/40 hover:shadow-elevated">
              All doctors
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          {specialists.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
              <p className="text-muted-foreground">Specialist profiles for this department are being added soon. In the meantime, our reception team can help you book the right consultant.</p>
              <Link to="/contact" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground">
                Contact reception <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {specialists.map((d) => <DoctorCard key={d.name} d={d} />)}
            </div>
          )}
        </div>
      </section>

      <section className="py-24">
        <div className="mx-auto max-w-4xl px-6">
          <div className="mb-12 text-center">
            <div className="mb-5 flex items-center justify-center gap-3">
              <span className="h-px w-10 bg-gold" />
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">FAQ</p>
              <span className="h-px w-10 bg-gold" />
            </div>
            <h2 className="font-display text-3xl font-semibold text-primary md:text-4xl">Frequently asked questions</h2>
          </div>
          <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card px-6 shadow-soft">
            {dept.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-border">
                <AccordionTrigger className="text-left font-display text-base font-semibold text-foreground hover:text-primary">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-linear-to-br from-primary to-secondary py-20 text-primary-foreground">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Ready when you are</p>
          <h2 className="mt-4 font-display text-3xl font-semibold md:text-5xl">Book a consultation with our {dept.department} team</h2>
          <p className="mt-5 text-base text-primary-foreground/80">Same-day appointments often available. Our care coordinators will guide you through every step.</p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold/90">
              <Link to="/doctors"><CalendarHeart className="mr-2 h-4 w-4" />Book Appointment</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10">
              <Link to="/contact">Contact us</Link>
            </Button>
          </div>

          <div className="mt-16 border-t border-primary-foreground/10 pt-10">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Explore more</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {departments.filter((d) => d.name !== dept.name).slice(0, 6).map((d) => (
                <Link key={d.name} to={departmentUrl(d.name)} className="rounded-full border border-primary-foreground/20 px-4 py-2 text-xs font-medium transition-colors hover:border-gold hover:text-gold">
                  {d.department}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
